import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import useConferenceRecording, { MAX_RECORDING_DURATION_MS } from "hooks/useConferenceRecording";
import { copyDumpToClipboard } from "lib/diagnosticDump";
import { markRecordingAnnounced } from "lib/recordingSegmentUploader";
import { SYSTEM_MESSAGE_PREFIX } from "lib/emitter";
import {
  buildTranscriptDownloadUrl,
  waitForTranscription,
} from "lib/recordingTranscriptWatcher";
import { waitForMeetingArtifacts } from "lib/aiApi";
import { getProduceStreams, liveChatUserName, recordingParticipantId } from "lib/liveStreamHelpers";
import providers from "lib/providers";
import { BackgroundMode } from "hooks/useBlurController";
import { ConnectionStatus } from "hooks/useConnectionReconnect";
import { NetworkQualityLevel } from "hooks/useNetworkQuality";
import { RemoteQuality } from "hooks/useRemoteNetworkQualities";
import { MediaConstraints, ProduceStream, ProduceUser, UserListItemIcon } from "types";
import BackgroundSelector from "components/BackgroundSelector";
import NetworkQualityBadge from "components/NetworkQualityBadge";
import BaseButton from "components/BaseButton";
import BaseIcon from "components/BaseIcon";
import ChatTimer from "components/ChatTimer";
import DotFlashingAnimation from "components/DotFlashingAnimation";
import StreamView from "components/StreamView";
import VideoControlButton from "components/VideoControlButton";
import ConferenceShareModal from "../ConferenceShareModal";

interface Props {
  threadId: string | undefined;
  conferenceUuid: string;
  loading: boolean;
  userList: LiveChatUser[];
  token: string;
  showConfirmationExit: boolean;
  audioInputs: MediaDeviceInfo[];
  videoInputs: MediaDeviceInfo[];
  users: ProduceUser[];
  me: ProduceUser | null;
  isConferenceOwner: boolean;
  muteUser: (user: LiveChatUser) => void;
  managedConstraints: MediaConstraints | null;
  streamStartedDate: string;
  onChangeConstraints: (constraints: MediaConstraints) => void;
  onCloseConference: () => void;
  onCloseStream: () => void | Promise<void>;
  groupThreadId?: string;
  groupId?: string;
  connectionStatus?: ConnectionStatus;
  /** Seconds remaining until the auto-rejoin path fires, or `null` when
   *  there is no pending rejoin. Comes from `useConnectionReconnect`. */
  rejoinInSeconds?: number | null;
  /** Manual "try now" handler — bypasses the countdown. */
  rejoinNow?: () => void | Promise<void>;
  /** Producer StreamingHelper exposed so the diagnostic copy button can
   *  snapshot its `peerConnection.getStats()`. */
  webRTC?: { webRTCData?: { producer?: { client?: { getStats?: () => Promise<unknown> } }; consumer?: { client?: { getStats?: () => Promise<unknown> } } } } | null;
  networkQuality?: NetworkQualityLevel;
  remoteQualities?: Record<string, RemoteQuality>;
  onSetBackgroundImage?: (imageUrl: string | null) => void;
  onSetBackgroundMode?: (mode: BackgroundMode) => void;
}

async function sendSystemMessage(threadId: string, text: string) {
  try {
    await providers.MessagesProvider.sendExtendedMessages({
      data: { text: `${SYSTEM_MESSAGE_PREFIX}${text}`, threadIds: [threadId] },
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to send system message:", e);
  }
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function ConferenceView({
  users,
  me,
  muteUser,
  isConferenceOwner,
  conferenceUuid,
  streamStartedDate,
  onCloseStream,
  onCloseConference,
  threadId,
  loading,
  userList,
  token,
  showConfirmationExit,
  audioInputs,
  videoInputs,
  managedConstraints,
  onChangeConstraints,
  groupThreadId,
  groupId,
  connectionStatus,
  rejoinInSeconds,
  rejoinNow,
  webRTC,
  networkQuality,
  remoteQualities,
  onSetBackgroundImage,
  onSetBackgroundMode,
}: Props) {
  const [showShare, setShowShare] = useState(false);
  const [showBackgroundSelector, setShowBackgroundSelector] = useState(false);
  const bgSelectorRef = useRef<HTMLDivElement>(null);
  const bgToggleBtnRef = useRef<HTMLSpanElement>(null);
  const [backgroundMode, setBackgroundMode] = useState<BackgroundMode>("none");
  const [streams, setStreams] = useState<ProduceStream[]>([]);
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const selectedStreamIdRef = useRef<string | null>(null);
  const { recording, recordingStartedAt, uploading, uploadProgress, uploadError, startRecording, stopRecording, updateStreams } = useConferenceRecording();
  const callStartSent = useRef(false);
  const callStartTime = useRef<number>(Date.now());
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  const groupThreadIdRef = useRef(groupThreadId);
  groupThreadIdRef.current = groupThreadId;
  const [autoRecordVideo, setAutoRecordVideo] = useState(false);
  const autoRecordTriggered = useRef(false);
  // Recording-state of the *remote* recorder, derived from system messages
  // broadcast in the group thread. The conference owner (who actually owns
  // the recorder) uses its local `recordingStartedAt` for instant feedback;
  // every other participant watches this value to drive the REC indicator.
  // 1:1 / non-group calls (no groupThreadId) fall back to owner-only.
  const [remoteRecordingStartedAt, setRemoteRecordingStartedAt] = useState<number | null>(null);

  const userListItemIcon = useMemo<UserListItemIcon>(() => {
    return {
      onIconCLick: (user: LiveChatUser) => muteUser(user),
      showIcon: isConferenceOwner,
      showIconOnMe: false,
      meId: me?.user.id,
    };
  }, [me, muteUser, isConferenceOwner]);

  useEffect(() => {
    if (uploadError) {
      // eslint-disable-next-line no-console
      console.error("Recording upload error:", uploadError);
    }
  }, [uploadError]);

  // Dismiss background selector via Escape or outside click.
  // The toggle button is excluded so it can still close the panel itself.
  useEffect(() => {
    if (!showBackgroundSelector) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (bgSelectorRef.current?.contains(target)) return;
      if (bgToggleBtnRef.current?.contains(target)) return;
      setShowBackgroundSelector(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowBackgroundSelector(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [showBackgroundSelector]);

  // Send "call started" system message once
  useEffect(() => {
    if (groupThreadId && isConferenceOwner && !callStartSent.current) {
      callStartSent.current = true;
      callStartTime.current = Date.now();
      sendSystemMessage(groupThreadId, "Video call started");
    }
  }, [groupThreadId, isConferenceOwner]);

  // Watch group-thread system messages so non-owner participants can show a
  // live REC indicator while the owner is recording. Late joiners get the
  // current state from the most recent system message; afterwards a thread
  // subscription pushes updates in real time.
  useEffect(() => {
    if (!groupThreadId) return;
    if (isConferenceOwner) return; // owner uses local recordingStartedAt

    let cancelled = false;
    let sub: { unsubscribe: () => void } | null = null;

    const applyFromText = (text: string | null | undefined, timeIso: string | null | undefined) => {
      if (!text || !text.startsWith(SYSTEM_MESSAGE_PREFIX)) return;
      const body = text.replace(SYSTEM_MESSAGE_PREFIX, "").trim();
      if (body.startsWith("Recording started")) {
        const t = timeIso ? Date.parse(timeIso) : Date.now();
        setRemoteRecordingStartedAt(Number.isFinite(t) ? t : Date.now());
      } else if (
        body.startsWith("Recording available:") ||
        body.startsWith("Recording stopped") ||
        body.startsWith("Video call ended")
      ) {
        setRemoteRecordingStartedAt(null);
      }
    };

    (async () => {
      try {
        const resp = await providers.MessagesProvider.find({
          data: { threadId: groupThreadId, sort: "TIME,DESC", page: 0 },
        });
        if (cancelled) return;
        // Walk newest → oldest until we hit a recording-relevant event.
        // First match wins: a recent stop ⇒ idle, a recent start ⇒ active.
        for (const m of resp.result) {
          const txt = m.text || "";
          if (!txt.startsWith(SYSTEM_MESSAGE_PREFIX)) continue;
          const body = txt.replace(SYSTEM_MESSAGE_PREFIX, "").trim();
          if (
            body.startsWith("Recording available:") ||
            body.startsWith("Recording stopped") ||
            body.startsWith("Video call ended")
          ) {
            break;
          }
          if (body.startsWith("Recording started")) {
            const t = Date.parse(m.time);
            setRemoteRecordingStartedAt(Number.isFinite(t) ? t : Date.now());
            break;
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[recording] failed to fetch initial recording state:", e);
      }

      try {
        const s = await providers.MessagesProvider.subscribeToThread(groupThreadId, (msg: Message) => {
          if (cancelled) return;
          applyFromText(msg.text, msg.time);
        });
        if (cancelled) {
          s.unsubscribe();
          return;
        }
        sub = s;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[recording] subscribeToThread failed:", e);
      }
    })();

    return () => {
      cancelled = true;
      sub?.unsubscribe();
      setRemoteRecordingStartedAt(null);
    };
  }, [groupThreadId, isConferenceOwner]);

  // Load group's autoRecordVideo setting once when we know which group we're in.
  // Only the conference owner can start recording, so other roles (members, guests)
  // don't need this — and a guest's auth token would not let them search groups anyway.
  useEffect(() => {
    if (!groupId) return;
    if (!isConferenceOwner) return;
    let cancelled = false;
    providers.GroupsProvider.search({ data: { id: String(groupId) } })
      .then((groups) => {
        if (!cancelled && groups && groups[0]) {
          setAutoRecordVideo(!!groups[0].autoRecordVideo);
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("Failed to load group settings for auto-record:", e);
      });
    return () => { cancelled = true; };
  }, [groupId, isConferenceOwner]);

  const stopAndUpload = useCallback(async (): Promise<void> => {
    const tid = groupThreadIdRef.current;
    const result = await stopRecording();
    if (!result) {
      if (tid) {
        // eslint-disable-next-line no-console
        console.warn("Recording stopped but no file was produced (too short or upload failed)");
        // Broadcast a stop signal anyway so non-owner participants can clear
        // their REC indicator — otherwise it would stay lit until the call
        // ends (the indicator only flips off on "Recording available:",
        // "Recording stopped" or "Video call ended").
        await sendSystemMessage(tid, "Recording stopped");
      }
      return;
    }
    if (!tid) return;

    await sendSystemMessage(tid, `Recording available: ${result.url}`);

    // Transcription is a separate, asynchronous pipeline — watch for its
    // completion in the background and post a follow-up system message
    // with a download link (.txt) when it's ready. Detached from the
    // recording lifecycle so we don't block the caller (and so the user
    // can close the call UI immediately after the video is uploaded).
    if (!result.id) return;
    const recordingId = result.id;
    // Confirm to the server so its auto-announce safety net won't re-post.
    void markRecordingAnnounced(recordingId);
    void (async () => {
      try {
        const watch = await waitForTranscription(recordingId);
        if (watch.status === "failed") {
          const err = watch.recording?.transcription?.error || "unknown error";
          await sendSystemMessage(tid, `Transcript failed: ${err}`);
          return;
        }
        if (watch.status !== "done") return; // timeout / missing → silent

        const url = buildTranscriptDownloadUrl(recordingId, "txt");
        const segs = watch.recording?.transcription?.segments?.length ?? 0;
        await sendSystemMessage(
          tid,
          `Transcript ready (${segs} segment${segs === 1 ? "" : "s"}): ${url}`,
        );

        // Meeting Coach runs server-side after transcription. Wait for the
        // artifact row, then post a chat-side affordance to open insights.
        // Silent on disabled / timeout / failure to avoid noise — admins can
        // inspect via DB or /health/services.
        const a = await waitForMeetingArtifacts(recordingId);
        if (a.status !== "done" || !a.artifacts) return;
        const bits: string[] = [];
        const topics = a.artifacts.topics.length;
        const actions = a.artifacts.actionItems.length;
        if (topics) bits.push(`${topics} topic${topics === 1 ? "" : "s"}`);
        if (actions) bits.push(`${actions} action item${actions === 1 ? "" : "s"}`);
        const detail = bits.length ? ` (${bits.join(", ")})` : "";
        await sendSystemMessage(
          tid,
          `Meeting insights ready${detail} [[insights:${recordingId}]]`,
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[recording] post-record watcher crashed:", e);
      }
    })();
  }, [stopRecording]);

  // Auto-stop at the hard duration cap so the recording is uploaded gracefully
  // instead of growing past the server's 2 GB ceiling and being lost. Only the
  // owner records (local `recording` is true only for them), so only they arm
  // the timer. The absolute deadline is recordingStartedAt + cap; re-arming on
  // dependency changes recomputes the remaining time, so it never drifts.
  useEffect(() => {
    if (!recording || !recordingStartedAt) return;
    const remaining = MAX_RECORDING_DURATION_MS - (Date.now() - recordingStartedAt);
    const timer = setTimeout(() => {
      const tid = groupThreadIdRef.current;
      if (tid) {
        void sendSystemMessage(
          tid,
          `Recording auto-stopped at ${Math.round(MAX_RECORDING_DURATION_MS / 60000)} min limit`,
        );
      }
      void stopAndUpload();
    }, Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [recording, recordingStartedAt, stopAndUpload]);

  const onClose = useCallback(async () => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);

    try {
      // 1. Release camera/mic + close WebRTC transports IMMEDIATELY so the OS
      //    camera/mic indicators clear the instant you leave — don't wait for
      //    the recording upload to finalize. The recording has been streaming
      //    live; stopAndUpload below seals it, losing at most a sub-second
      //    frozen tail. Each later step is best-effort so a failure can't skip
      //    teardown/navigation (previously a throw in stopAndUpload/sendSystem-
      //    Message left the camera lit until the page was reloaded).
      try { await onCloseStream(); } catch { /* ignore */ }

      // 2. Stop recording + finalize upload.
      if (recordingRef.current) {
        try { await stopAndUpload(); } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[conf] stopAndUpload failed:", e);
        }
      }

      // 3. Send "call ended" system message (owner only).
      if (groupThreadIdRef.current && isConferenceOwner) {
        const duration = formatDuration(Date.now() - callStartTime.current);
        try {
          await sendSystemMessage(groupThreadIdRef.current, `Video call ended (duration: ${duration})`);
        } catch { /* ignore */ }
      }

      // 4. End the group call on backend (owner only).
      if (groupId && isConferenceOwner) {
        try {
          await providers.GroupsProvider.endGroupCall(String(groupId));
        } catch {
          // May fail if already ended
        }
      }
    } finally {
      // 5. Navigate away
      onCloseConference();
    }
  }, [closing, isConferenceOwner, groupId, onCloseStream, onCloseConference, stopAndUpload]);

  useEffect(() => {
    const result: ProduceStream[] = [];
    if (me) {
      result.push(...getProduceStreams(me));
    }
    users.forEach((user) => result.push(...getProduceStreams(user)));

    // Decorate each stream with its current quality bars + auto-pause flag.
    // We resolve quality from two sources: own transport stats for `me`
    // (the existing `networkQuality` prop), per-consumer stats keyed by
    // remote user id for everyone else. The screen-share tile reuses the
    // owner's quality — they share one transport.
    const decorated = result.map<ProduceStream>((s) => {
      const userId = s.user.id;
      const isMe = !!me && userId === me.user.id;
      const remote = remoteQualities?.[userId];
      return {
        ...s,
        quality: isMe ? networkQuality : remote?.quality,
        videoAutoPaused: !isMe && !!remote?.videoAutoPaused && !s.isScreen,
      };
    });
    setStreams(decorated);
  }, [me, users, networkQuality, remoteQualities]);

  const handleSelectedStreamChange = useCallback((streamId: string | null) => {
    selectedStreamIdRef.current = streamId;
    if (recording) {
      updateStreams(streams, streamId);
    }
  }, [recording, streams, updateStreams]);

  const prevStreamsRef = useRef(streams);
  useEffect(() => {
    if (recording && prevStreamsRef.current !== streams) {
      updateStreams(streams, selectedStreamIdRef.current);
    }
    prevStreamsRef.current = streams;
  }, [streams, recording, updateStreams]);

  const beginRecording = useCallback((notice: string): boolean => {
    // Dedupe by userId — getProduceStreams() emits a separate "__screen" entry
    // for the same user when they share screen; the backend should see each
    // participant only once in the recording metadata.
    const seenUserIds = new Set<string>();
    const participants: Array<{ userId: string; screenName: string }> = [];
    streams.forEach((s) => {
      // Prefer the account id (resolvable to the current name) over the
      // ephemeral livechat session id — see recordingParticipantId.
      const userId = recordingParticipantId(s.user) || s.id;
      if (seenUserIds.has(userId)) return;
      seenUserIds.add(userId);
      participants.push({ userId, screenName: liveChatUserName(s.user) });
    });
    const started = startRecording(streams, me?.user.id, {
      conferenceId: conferenceUuid,
      threadId: groupThreadIdRef.current || threadId,
      participants,
    });
    if (started && groupThreadIdRef.current) {
      void sendSystemMessage(groupThreadIdRef.current, notice);
    }
    return !!started;
  }, [streams, me, startRecording, conferenceUuid, threadId]);

  // Auto-start recording when group has autoRecordVideo enabled.
  // Fires once per conference session (autoRecordTriggered ref) — manual stop
  // by the owner will NOT trigger another auto-restart.
  useEffect(() => {
    if (!autoRecordVideo) return;
    if (!isConferenceOwner) return;
    if (autoRecordTriggered.current) return;
    if (recording) return;
    if (closing) return;
    if (streams.length === 0) return;

    if (beginRecording("Recording started automatically")) {
      autoRecordTriggered.current = true;
    }
  }, [autoRecordVideo, isConferenceOwner, streams, recording, closing, beginRecording]);

  const recordingToggling = useRef(false);
  const handleToggleRecording = useCallback(async () => {
    if (recordingToggling.current) return;
    recordingToggling.current = true;
    try {
      if (recording) {
        await stopAndUpload();
      } else {
        beginRecording("Recording started");
      }
    } finally {
      recordingToggling.current = false;
    }
  }, [recording, beginRecording, stopAndUpload]);

  const slotVideoScreen = (() => {
    if (!users.length && !streams.length) {
      return <DotFlashingAnimation className="conference-view__dot-animation" />;
    }
  })();

  const slotHeaderButtons = (
    <>
      {networkQuality !== undefined && (
        <NetworkQualityBadge quality={networkQuality} className="conference-view__network-badge" />
      )}
      <BaseButton className="stream-view__header-btn __share" name={"share"} onClick={() => setShowShare(true)}>
        <BaseIcon className="stream-view__header-icon">share-outline</BaseIcon>
      </BaseButton>
    </>
  );

  // Owner sees its local recording start time (instant); other participants
  // see the time we derived from broadcast system messages.
  const effectiveRecordingStartedAt = isConferenceOwner ? recordingStartedAt : remoteRecordingStartedAt;

  const slotTimer = (
    <>
      <ChatTimer showTimeIfNegative streamStartedDate={streamStartedDate}>
        {"Live chat"}
      </ChatTimer>
      {effectiveRecordingStartedAt != null && (
        <ChatTimer streamStartedDate={new Date(effectiveRecordingStartedAt).toISOString()}>REC</ChatTimer>
      )}
    </>
  );

  const handleBackgroundModeChange = useCallback((mode: BackgroundMode) => {
    setBackgroundMode(mode);
    onSetBackgroundMode?.(mode);
    if (mode === "none") {
      onChangeConstraints({ blurEnabled: false });
    } else if (mode === "blur") {
      onSetBackgroundImage?.(null);
      onChangeConstraints({ blurEnabled: true });
    }
    // "image" mode is handled by handleBackgroundImageSelect
  }, [onSetBackgroundMode, onSetBackgroundImage, onChangeConstraints]);

  const handleBackgroundImageSelect = useCallback((imageUrl: string) => {
    setBackgroundMode("image");
    onSetBackgroundImage?.(imageUrl);
    onSetBackgroundMode?.("image");
    onChangeConstraints({ blurEnabled: true });
  }, [onSetBackgroundImage, onSetBackgroundMode, onChangeConstraints]);

  const slotControlButtons = (
    <>
      {isConferenceOwner && (
        <VideoControlButton
          className={`panel-control-buttons__btn${recording ? " __recording-active" : ""}`}
          name="toggle-recording"
          toggle={recording}
          borderless
          onClick={handleToggleRecording}
          disabled={uploading || closing}
        >
          <BaseIcon>{recording ? "record_on" : "record_off"}</BaseIcon>
        </VideoControlButton>
      )}
      {onSetBackgroundImage && (
        <span ref={bgToggleBtnRef} style={{ display: "inline-flex" }}>
          <VideoControlButton
            className="panel-control-buttons__btn"
            name="toggle-backgrounds"
            toggle={showBackgroundSelector}
            borderless
            onClick={() => setShowBackgroundSelector((v) => !v)}
          >
            <BaseIcon>fa-image</BaseIcon>
          </VideoControlButton>
        </span>
      )}
    </>
  );

  // Standalone Copy-diagnostic affordance, always visible during the
  // conference so the user can grab the ring buffer the moment something
  // feels off — including BEFORE any disconnect banner appears. The
  // duplicate button INSIDE the failure banner stays as a redundant
  // shortcut for the no-other-UI-visible case. Mirrors PageDirectCall.
  const copyDiagnosticNow = useCallback(async () => {
    try {
      // Wrap each getStats in a 3s timeout — after transport teardown the
      // mediasoup-client transport.getStats() can hang or reject; without
      // the race the Copy button could lock the UI on an unresolved
      // promise.
      const withTimeout = <T,>(p: Promise<T>, label: string): Promise<T> =>
        Promise.race([
          p,
          new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} timeout 3000ms`)), 3000)),
        ]);
      const producerClient = webRTC?.webRTCData?.producer?.client;
      const consumerClient = webRTC?.webRTCData?.consumer?.client;
      const rtcEntries: Array<{ name: string; getStats?: () => Promise<unknown> }> = [];
      if (producerClient?.getStats) {
        rtcEntries.push({ name: "producer", getStats: () => withTimeout(producerClient.getStats(), "producer getStats") });
      }
      if (consumerClient?.getStats) {
        rtcEntries.push({ name: "consumer", getStats: () => withTimeout(consumerClient.getStats(), "consumer getStats") });
      }
      await copyDumpToClipboard({
        rtc: rtcEntries,
        extra: { connectionStatus, rejoinInSeconds, networkQuality },
      });
      // eslint-disable-next-line no-alert
      window.alert("Диагностика скопирована в буфер обмена. Отправьте её разработчику.");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[diag] copy failed", e);
    }
  }, [webRTC, connectionStatus, rejoinInSeconds, networkQuality]);

  return (
    <>
      <ConferenceShareModal show={showShare} conferenceUuid={conferenceUuid} onClose={() => setShowShare(false)} />

      {/* Always-on Copy diagnostic — small floating affordance top-right
          of the conference view. zIndex=50 keeps it above the conference
          video stack but BELOW BaseModal/IncomingCallModal (z=10000) so
          it never steals clicks from modals. The banner-embedded copy
          below stays as a redundant shortcut when the failure banner has
          the user's attention. */}
      <button
        type="button"
        aria-label="Copy diagnostic logs"
        title="Скопировать диагностические логи в буфер обмена (для отправки разработчику)"
        onClick={copyDiagnosticNow}
        style={{ position: "fixed", top: 8, right: 8, zIndex: 50, padding: "4px 10px", borderRadius: 4, border: "1px solid rgba(255,255,255,0.4)", background: "rgba(0,0,0,0.4)", color: "#fff", cursor: "pointer", fontSize: 12 }}
      >
        Copy diagnostic
      </button>

      {!closing && connectionStatus && connectionStatus !== "connected" && (
        <div className="conference-view__connection-banner" data-status={connectionStatus}>
          {connectionStatus === "reconnecting" && "Reconnecting..."}
          {connectionStatus === "disconnected" && "Connection lost. Reconnecting soon..."}
          {connectionStatus === "failed" && (
            rejoinInSeconds != null && rejoinInSeconds > 0
              ? `Connection failed. Auto-rejoining in ${rejoinInSeconds}s...`
              : "Connection failed. Please rejoin the call."
          )}
          {connectionStatus === "failed" && rejoinNow && (
            <button
              type="button"
              className="conference-view__connection-banner-btn"
              onClick={() => { void rejoinNow(); }}
              style={{ marginLeft: 12, padding: "4px 12px", borderRadius: 4, border: "1px solid currentColor", background: "transparent", color: "inherit", cursor: "pointer", fontSize: 13 }}
            >
              Try now
            </button>
          )}
          <button
            type="button"
            className="conference-view__connection-banner-diag-btn"
            title="Скопировать диагностические логи в буфер обмена (для отправки разработчику)"
            onClick={async () => {
              try {
                // Capture each client reference once and only bind when both
                // the client and the method exist. Optional chaining on
                // `.bind` is already safe (short-circuits before the
                // argument is evaluated), but the explicit-temp variant
                // makes the intent obvious and survives later refactors.
                const producerClient = webRTC?.webRTCData?.producer?.client;
                const consumerClient = webRTC?.webRTCData?.consumer?.client;
                const rtcEntries: Array<{ name: string; getStats?: () => Promise<unknown> }> = [];
                if (producerClient?.getStats) {
                  rtcEntries.push({ name: "producer", getStats: producerClient.getStats.bind(producerClient) });
                }
                if (consumerClient?.getStats) {
                  rtcEntries.push({ name: "consumer", getStats: consumerClient.getStats.bind(consumerClient) });
                }
                await copyDumpToClipboard({
                  rtc: rtcEntries,
                  extra: { connectionStatus, rejoinInSeconds, networkQuality },
                });
                // eslint-disable-next-line no-alert
                window.alert("Диагностика скопирована в буфер обмена. Отправьте её разработчику.");
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error("[diag] copy failed", e);
              }
            }}
            style={{ marginLeft: 12, padding: "4px 12px", borderRadius: 4, border: "1px solid currentColor", background: "transparent", color: "inherit", cursor: "pointer", fontSize: 13 }}
          >
            Copy diagnostic
          </button>
        </div>
      )}

      {uploading && (
        <div
          className="conference-view__upload-banner"
          data-phase={uploadProgress?.phase || "uploading"}
        >
          {uploadProgress?.phase === "processing" ? (
            <>
              <span className="conference-view__upload-banner-spinner" />
              <span className="conference-view__upload-banner-label">
                Processing recording on server…
              </span>
            </>
          ) : (
            <>
              <span className="conference-view__upload-banner-label">
                Uploading recording…
              </span>
              {uploadProgress && uploadProgress.total > 0 ? (
                <>
                  <span className="conference-view__upload-banner-bar">
                    <span
                      className="conference-view__upload-banner-bar-fill"
                      style={{
                        width: `${Math.min(100, Math.round((uploadProgress.loaded / uploadProgress.total) * 100))}%`,
                      }}
                    />
                  </span>
                  <span className="conference-view__upload-banner-stats">
                    {`${Math.round((uploadProgress.loaded / uploadProgress.total) * 100)}% — ${(uploadProgress.loaded / 1024 / 1024).toFixed(1)} / ${(uploadProgress.total / 1024 / 1024).toFixed(1)} MB`}
                  </span>
                </>
              ) : (
                <span className="conference-view__upload-banner-spinner" />
              )}
            </>
          )}
        </div>
      )}

      {showBackgroundSelector && onSetBackgroundImage && (
        <div ref={bgSelectorRef} className="conference-view__background-selector">
          <BackgroundSelector
            currentMode={backgroundMode}
            onSelectMode={handleBackgroundModeChange}
            onSelectImage={handleBackgroundImageSelect}
            onClose={() => setShowBackgroundSelector(false)}
          />
        </div>
      )}

      <StreamView
        threadId={threadId}
        token={token}
        loading={loading || closing}
        userList={userList}
        showConfirmationExit={showConfirmationExit}
        audioInputs={audioInputs}
        videoInputs={videoInputs}
        managedConstraints={managedConstraints}
        onChangeConstraints={onChangeConstraints}
        onClose={onClose}
        className="conference-view"
        produce
        produceUser={null}
        enableUserSearch
        allowGuestMessage
        me={me}
        userListItemIcon={userListItemIcon}
        slotHeaderButtons={slotHeaderButtons}
        includeGuestsList
        slotTimer={slotTimer}
        slotVideoScreen={slotVideoScreen}
        streams={streams}
        hideVolumeControl
        hideChatOnStart
        slotControlButtons={slotControlButtons}
        onSelectedStreamChange={handleSelectedStreamChange}
      />
    </>
  );
}
